import React, { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import {
  SellableAs,
  DeviationTypes,
} from '@wix/da-shared-react/pkg/types/deviation';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import FieldsGroup from '../../layouts/common/FieldsGroup';
import AttachmentsField from '../AttachmentsField';
import PriceField from '../PriceField';
import { SubmitFormContext } from '../../context';
import { withConditionalField } from '../../hoc/withConditionalField';
import { useSubmitFormStatus } from '../../hooks/useSubmitFormStatus';

import s from './PcpFieldsWrapper.scss';

interface Props {
  fieldClassName?: string;
}

const PcpFieldsWrapper: React.FC<Props> = ({ fieldClassName }) => {
  const { t } = useTranslation();
  const hasCoreMembership = useSelector(getCurrentUserHasCore);
  const { isFormBusy } = useSubmitFormStatus();
  const { initResponse, params } = useContext(SubmitFormContext) || {};
  const deviation = initResponse?.deviation;
  const inTier = Boolean(params?.tierId || deviation?.primaryTier);

  const { register, watch } = useFormContext();
  useEffect(() => {
    register('isPurchasable');
  }, [register]);

  const { isPurchasable, showAttachments, originalAttached } = watch([
    'isPurchasable',
    'showAttachments',
    'originalAttached',
  ]);

  const allowPcpUpload =
    (hasCoreMembership || typeof deviation?.pcp !== 'undefined') && !isFormBusy;

  const deviationHasPcpAlready = typeof deviation?.pcp !== 'undefined';
  const showPriceField =
    (hasCoreMembership || deviationHasPcpAlready) &&
    (isPurchasable || originalAttached) &&
    showAttachments;

  return (
    <FieldsGroup className={s['root']}>
      <AttachmentsField
        className={fieldClassName}
        deviation={deviation}
        inTier={inTier}
        hasCoreMembership={hasCoreMembership}
        allowUpload={allowPcpUpload}
      />
      {showPriceField && (
        <PriceField
          className={fieldClassName}
          label={t('deviationSubmit.form.price.label.updated')}
          withEarnings
        />
      )}
    </FieldsGroup>
  );
};
PcpFieldsWrapper.displayName = 'PcpFieldsWrapper';

export default withConditionalField<Props>(
  PcpFieldsWrapper,
  ({ deviation, features }) => {
    const deviationType = getDeviationType(deviation);
    const isLiterature = deviationType === DeviationTypes.LITERATURE;
    if (isLiterature) {
      return false;
    }

    // If the deviation is an adoptable it can't be sold as pcp, but we still want to show this to render a message saying exactly that.
    return Boolean(
      deviation?.isAdoptable ||
        deviation?.isPurchasable ||
        deviation?.extended?.sellableAs?.includes(SellableAs.Pcp)
    );
  }
);
